import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Key, Bell, Mail, Cloud, GitBranch, Download, Settings,
  Plus, Trash2, Check, AlertCircle, ExternalLink, RefreshCw,
  ChevronRight, Slack, Send, Archive, Link2, Users, Upload,
  Rocket, Tag, Webhook, FileText, X, Loader2, Globe, BarChart3
} from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

// ─── Shared styles ───────────────────────────────────────────────────────────
const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4 md:p-6';
const input = 'w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none min-h-[44px]';
const btnPrimary = 'px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0F0F0F] font-bold text-sm rounded hover:bg-[#E68500] transition-colors';
const btnSecondary = 'px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-[#A0A0A0] text-sm rounded hover:border-[#FF4D00] hover:text-[#F5F5F5] transition-colors';
const label = 'block text-xs font-mono text-[#A0A0A0] mb-1';

const TABS = [
  { id: 'delivery', label: 'Delivery', icon: Bell },
  { id: 'sso', label: 'SSO', icon: Shield },
  { id: 'github-triggers', label: 'CI/CD', icon: GitBranch },
  { id: 'department-keys', label: 'BYOK', icon: Key },
  { id: 'bulk-export', label: 'Export', icon: Download },
  { id: 'onboarding', label: 'Setup', icon: Rocket },
  { id: 'guests', label: 'Guests', icon: Users },
  { id: 'ip-allowlist', label: 'IP Allow', icon: Globe },
  { id: 'bulk-import', label: 'Import', icon: Upload },
  { id: 'classification', label: 'Labels', icon: Tag },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'ropa', label: 'RoPA', icon: FileText },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];


// ═══════════════════════════════════════════════════════════════════════════════
// DELIVERY SETTINGS (#118, #119, #120, #121)
// ═══════════════════════════════════════════════════════════════════════════════
function DeliveryTab() {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newType, setNewType] = useState('slack');
  const [testing, setTesting] = useState(null);

  // Slack config
  const [slackUrl, setSlackUrl] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [slackVerbosity, setSlackVerbosity] = useState('summary');

  // Email config
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailFormat, setEmailFormat] = useState('html');

  // Webhook config
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  // Storage config
  const [storageProvider, setStorageProvider] = useState('s3');
  const [storageBucket, setStorageBucket] = useState('');
  const [storageAccessKey, setStorageAccessKey] = useState('');
  const [storageSecretKey, setStorageSecretKey] = useState('');

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await API.get('/api/delivery/preferences');
      setPrefs(res.data.preferences || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const addPref = async () => {
    let config = {};
    if (newType === 'slack') config = { webhook_url: slackUrl, channel: slackChannel, verbosity: slackVerbosity };
    else if (newType === 'email') config = { recipients: emailRecipients.split(',').map(e => e.trim()).filter(Boolean), format: emailFormat };
    else if (newType === 'webhook') config = { url: webhookUrl, secret: webhookSecret };
    else if (newType === 'storage') config = { provider: storageProvider, bucket: storageBucket, access_key_id: storageAccessKey, secret_access_key: storageSecretKey };

    try {
      await API.post('/api/delivery/preferences', { delivery_type: newType, config });
      setShowAdd(false);
      fetchPrefs();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const deletePref = async (id) => {
    if (!window.confirm('Delete this delivery preference?')) return;
    try {
      await API.delete(`/api/delivery/preferences/${id}`);
      fetchPrefs();
    } catch (e) { /* ignore */ }
  };

  const testDelivery = async (type) => {
    setTesting(type);
    try {
      if (type === 'slack') await API.post('/api/delivery/slack/test', { webhook_url: slackUrl, channel: slackChannel, verbosity: slackVerbosity });
      else if (type === 'email') await API.post('/api/delivery/email/test', { recipients: emailRecipients.split(',').map(e => e.trim()).filter(Boolean), format: emailFormat });
      else if (type === 'webhook') await API.post('/api/delivery/webhooks/test', { url: webhookUrl, secret: webhookSecret });
      else if (type === 'storage') await API.post('/api/delivery/storage/test', { provider: storageProvider, config: { bucket: storageBucket, access_key_id: storageAccessKey, secret_access_key: storageSecretKey } });
      alert('Test sent successfully');
    } catch (e) { alert('Test failed: ' + (e.response?.data?.detail || e.message)); }
    setTesting(null);
  };

  const typeIcons = { slack: Slack, email: Mail, webhook: Link2, storage: Cloud };
  const typeColors = { slack: '#E01E5A', email: '#22C55E', webhook: '#F59E0B', storage: '#3B82F6' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-['Barlow_Condensed']">Delivery Preferences</h2>
        <button onClick={() => setShowAdd(!showAdd)} className={btnPrimary}>
          <Plus size={14} className="inline mr-1" /> Add
        </button>
      </div>

      {/* Existing preferences */}
      {loading ? <p className="text-[#A0A0A0] text-sm">Loading...</p> : prefs.length === 0 ? (
        <div className={card}>
          <p className="text-[#A0A0A0] text-sm text-center py-8">No delivery preferences configured. Add one to get notified when frays complete.</p>
        </div>
      ) : prefs.map(p => {
        const Icon = typeIcons[p.delivery_type] || Bell;
        const color = typeColors[p.delivery_type] || '#A0A0A0';
        const config = typeof p.config === 'string' ? JSON.parse(p.config) : (p.config || {});
        return (
          <motion.div key={p.id} className={card} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: color + '20', color }}>
                  <Icon size={16} />
                </div>
                <div>
                  <span className="text-sm font-bold capitalize">{p.delivery_type}</span>
                  <p className="text-xs text-[#A0A0A0] font-mono">
                    {p.delivery_type === 'slack' && (config.channel || config.webhook_url?.slice(0, 40))}
                    {p.delivery_type === 'email' && (config.recipients || []).join(', ')}
                    {p.delivery_type === 'webhook' && config.url?.slice(0, 40)}
                    {p.delivery_type === 'storage' && `${config.provider}://${config.bucket}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded ${p.active ? 'bg-[#22C55E20] text-[#22C55E]' : 'bg-[#EF444420] text-[#EF4444]'}`}>
                  {p.active ? 'Active' : 'Inactive'}
                </span>
                <button onClick={() => deletePref(p.id)} className="p-2 min-h-[44px] min-w-[44px] text-[#A0A0A0] hover:text-[#EF4444]"><Trash2 size={14} /></button>
              </div>
            </div>
          </motion.div>
        );
      })}

      {/* Add new preference */}
      <AnimatePresence>
        {showAdd && (
          <motion.div className={card} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <h3 className="text-sm font-bold mb-4">New Delivery Preference</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {['slack', 'email', 'webhook', 'storage'].map(t => {
                const Icon = typeIcons[t];
                return (
                  <button key={t} onClick={() => setNewType(t)}
                    className={`p-3 min-h-[44px] rounded border text-xs font-mono capitalize flex items-center gap-2 ${newType === t ? 'border-[#FF4D00] text-[#FF4D00] bg-[#FF4D0010]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#444]'}`}>
                    <Icon size={14} /> {t}
                  </button>
                );
              })}
            </div>

            {newType === 'slack' && (
              <div className="space-y-3">
                <div><label className={label}>Slack Webhook URL</label><input className={input} value={slackUrl} onChange={e => setSlackUrl(e.target.value)} placeholder="https://hooks.slack.com/services/..." /></div>
                <div><label className={label}>Channel (optional)</label><input className={input} value={slackChannel} onChange={e => setSlackChannel(e.target.value)} placeholder="#fray-results" /></div>
                <div><label className={label}>Verbosity</label>
                  <select className={input} value={slackVerbosity} onChange={e => setSlackVerbosity(e.target.value)}>
                    <option value="summary">Summary</option><option value="full">Full (with transcript excerpts)</option>
                  </select>
                </div>
              </div>
            )}

            {newType === 'email' && (
              <div className="space-y-3">
                <div><label className={label}>Recipients (comma-separated)</label><input className={input} value={emailRecipients} onChange={e => setEmailRecipients(e.target.value)} placeholder="team@company.com, boss@company.com" /></div>
                <div><label className={label}>Format</label>
                  <select className={input} value={emailFormat} onChange={e => setEmailFormat(e.target.value)}>
                    <option value="html">HTML (styled)</option><option value="text">Plain text</option><option value="link">Link only</option>
                  </select>
                </div>
              </div>
            )}

            {newType === 'webhook' && (
              <div className="space-y-3">
                <div><label className={label}>Webhook URL</label><input className={input} value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="https://your-app.com/webhooks/fray" /></div>
                <div><label className={label}>Secret (HMAC signing)</label><input className={input} type="password" value={webhookSecret} onChange={e => setWebhookSecret(e.target.value)} placeholder="your-secret-key" /></div>
              </div>
            )}

            {newType === 'storage' && (
              <div className="space-y-3">
                <div><label className={label}>Provider</label>
                  <select className={input} value={storageProvider} onChange={e => setStorageProvider(e.target.value)}>
                    <option value="s3">Amazon S3</option><option value="gcs">Google Cloud Storage</option><option value="azure">Azure Blob Storage</option>
                  </select>
                </div>
                <div><label className={label}>Bucket / Container</label><input className={input} value={storageBucket} onChange={e => setStorageBucket(e.target.value)} placeholder="my-fray-exports" /></div>
                <div><label className={label}>Access Key ID</label><input className={input} value={storageAccessKey} onChange={e => setStorageAccessKey(e.target.value)} /></div>
                <div><label className={label}>Secret Access Key</label><input className={input} type="password" value={storageSecretKey} onChange={e => setStorageSecretKey(e.target.value)} /></div>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button onClick={addPref} className={btnPrimary}>Save</button>
              <button onClick={() => testDelivery(newType)} className={btnSecondary} disabled={testing === newType}>
                {testing === newType ? <RefreshCw size={14} className="inline animate-spin mr-1" /> : <Send size={14} className="inline mr-1" />}
                Test
              </button>
              <button onClick={() => setShowAdd(false)} className={btnSecondary}>Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SSO CONFIGURATION (#160)
// ═══════════════════════════════════════════════════════════════════════════════
function SSOTab({ workspaceId }) {
  const [sso, setSso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [provider, setProvider] = useState('saml');
  const [idpUrl, setIdpUrl] = useState('');
  const [entityId, setEntityId] = useState('');
  const [enforce, setEnforce] = useState(false);
  const [allowPassword, setAllowPassword] = useState(true);
  const [autoProvision, setAutoProvision] = useState(false);
  const [defaultRole, setDefaultRole] = useState('member');

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    API.get(`/api/workspaces/${workspaceId}/sso`)
      .then(res => {
        setSso(res.data);
        setProvider(res.data.provider || 'saml');
        setIdpUrl(res.data.idp_metadata_url || '');
        setEntityId(res.data.entity_id || '');
        setEnforce(res.data.enforce || false);
        setAllowPassword(res.data.allow_password_login !== false);
        setAutoProvision(res.data.auto_provision || false);
        setDefaultRole(res.data.default_role || 'member');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workspaceId]);

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/api/workspaces/${workspaceId}/sso`, {
        provider, idp_metadata_url: idpUrl, entity_id: entityId,
        enforce, allow_password_login: allowPassword, auto_provision: autoProvision,
        default_role: defaultRole, enabled: true,
      });
      alert('SSO configuration saved');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const testConnection = async () => {
    setTestResult(null);
    try {
      const res = await API.post(`/api/workspaces/${workspaceId}/sso/test`, { idp_metadata_url: idpUrl });
      setTestResult(res.data);
    } catch (e) { setTestResult({ success: false, error: e.response?.data?.detail || 'Test failed' }); }
  };

  const disable = async () => {
    if (!window.confirm('Disable SSO? Users will need to use password login.')) return;
    try {
      await API.delete(`/api/workspaces/${workspaceId}/sso`);
      setSso({ enabled: false });
      alert('SSO disabled');
    } catch (e) { alert('Failed'); }
  };

  if (!workspaceId) return <div className={card}><p className="text-[#A0A0A0] text-sm text-center py-8">Select a workspace to configure SSO.</p></div>;
  if (loading) return <p className="text-[#A0A0A0] text-sm">Loading...</p>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold font-['Barlow_Condensed']">Single Sign-On (SSO)</h2>

      <div className={card}>
        <div className="space-y-4">
          <div><label className={label}>SSO Provider</label>
            <select className={input} value={provider} onChange={e => setProvider(e.target.value)}>
              <option value="saml">SAML 2.0</option><option value="oidc">OpenID Connect (OIDC)</option>
            </select>
          </div>

          <div><label className={label}>IdP Metadata URL</label>
            <input className={input} value={idpUrl} onChange={e => setIdpUrl(e.target.value)}
              placeholder={provider === 'saml' ? 'https://idp.company.com/metadata.xml' : 'https://accounts.google.com/.well-known/openid-configuration'} />
          </div>

          <div><label className={label}>Entity ID / Issuer</label>
            <input className={input} value={entityId} onChange={e => setEntityId(e.target.value)} placeholder="https://getfray.ai/saml/workspace-id" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" checked={enforce} onChange={e => setEnforce(e.target.checked)} className="accent-[#FF4D00]" />
              <span className="text-sm">Enforce SSO (require for all users)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" checked={allowPassword} onChange={e => setAllowPassword(e.target.checked)} className="accent-[#FF4D00]" />
              <span className="text-sm">Allow password login fallback</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" checked={autoProvision} onChange={e => setAutoProvision(e.target.checked)} className="accent-[#FF4D00]" />
              <span className="text-sm">Auto-provision new users from IdP</span>
            </label>
          </div>

          <div><label className={label}>Default Role for Auto-Provisioned Users</label>
            <select className={input} value={defaultRole} onChange={e => setDefaultRole(e.target.value)}>
              <option value="member">Member</option><option value="viewer">Viewer</option><option value="admin">Admin</option>
            </select>
          </div>

          {testResult && (
            <div className={`p-3 rounded text-sm ${testResult.success ? 'bg-[#22C55E20] text-[#22C55E]' : 'bg-[#EF444420] text-[#EF4444]'}`}>
              {testResult.success ? <Check size={14} className="inline mr-1" /> : <AlertCircle size={14} className="inline mr-1" />}
              {testResult.message || testResult.error}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button onClick={save} className={btnPrimary} disabled={saving}>{saving ? 'Saving...' : 'Save SSO Config'}</button>
            <button onClick={testConnection} className={btnSecondary}>Test Connection</button>
            {sso?.enabled && <button onClick={disable} className="px-4 py-2 min-h-[44px] text-[#EF4444] border border-[#EF444440] rounded hover:bg-[#EF444410] text-sm">Disable SSO</button>}
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// GITHUB TRIGGERS (#166)
// ═══════════════════════════════════════════════════════════════════════════════
function GitHubTriggersTab() {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [repo, setRepo] = useState('');
  const [eventType, setEventType] = useState('pull_request');
  const [branchFilter, setBranchFilter] = useState('main');
  const [playbookId, setPlaybookId] = useState('');
  const [playbooks, setPlaybooks] = useState([]);

  useEffect(() => {
    API.get('/api/github/triggers').then(r => setTriggers(r.data.triggers || [])).catch(() => {}).finally(() => setLoading(false));
    API.get('/api/playbooks').then(r => setPlaybooks(r.data.playbooks || [])).catch(() => {});
  }, []);

  const createTrigger = async () => {
    try {
      const res = await API.post('/api/github/triggers', {
        repo, event_type: eventType, branch_filter: branchFilter, playbook_id: playbookId || null,
      });
      setTriggers([...triggers, res.data]);
      setShowAdd(false);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const deleteTrigger = async (id) => {
    if (!window.confirm('Delete this trigger?')) return;
    try {
      await API.delete(`/api/github/triggers/${id}`);
      setTriggers(triggers.filter(t => t.trigger_id !== id && t.id !== id));
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-['Barlow_Condensed']">GitHub CI/CD Triggers</h2>
        <button onClick={() => setShowAdd(!showAdd)} className={btnPrimary}><Plus size={14} className="inline mr-1" /> Add Trigger</button>
      </div>

      <p className="text-xs text-[#A0A0A0]">Automatically run a fray when a PR is created or updated. Add a GitHub webhook pointing to your Fray instance.</p>

      {loading ? <p className="text-[#A0A0A0] text-sm">Loading...</p> : triggers.length === 0 && !showAdd ? (
        <div className={card}><p className="text-[#A0A0A0] text-sm text-center py-8">No triggers configured.</p></div>
      ) : triggers.map((t, i) => (
        <motion.div key={t.trigger_id || t.id || i} className={card} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <GitBranch size={14} className="text-[#A855F7]" />
                <span className="text-sm font-bold font-mono">{t.repo}</span>
              </div>
              <p className="text-xs text-[#A0A0A0] mt-1">
                Event: <span className="text-[#F5F5F5]">{t.event_type}</span>
                {t.branch_filter && <> | Branch: <span className="text-[#F5F5F5]">{t.branch_filter}</span></>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded ${t.active ? 'bg-[#22C55E20] text-[#22C55E]' : 'bg-[#EF444420] text-[#EF4444]'}`}>
                {t.active ? 'Active' : 'Inactive'}
              </span>
              <button onClick={() => deleteTrigger(t.trigger_id || t.id)} className="p-2 min-h-[44px] min-w-[44px] text-[#A0A0A0] hover:text-[#EF4444]"><Trash2 size={14} /></button>
            </div>
          </div>
          {t.webhook_secret && (
            <div className="mt-3 p-2 bg-[#0F0F0F] rounded">
              <p className="text-xs text-[#A0A0A0] mb-1">Webhook Secret (add to GitHub):</p>
              <code className="text-xs text-[#FF4D00] break-all">{t.webhook_secret}</code>
            </div>
          )}
        </motion.div>
      ))}

      <AnimatePresence>
        {showAdd && (
          <motion.div className={card} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <h3 className="text-sm font-bold mb-4">New GitHub Trigger</h3>
            <div className="space-y-3">
              <div><label className={label}>Repository (owner/repo)</label><input className={input} value={repo} onChange={e => setRepo(e.target.value)} placeholder="myorg/myrepo" /></div>
              <div><label className={label}>Event Type</label>
                <select className={input} value={eventType} onChange={e => setEventType(e.target.value)}>
                  <option value="pull_request">Pull Request</option><option value="push">Push</option>
                </select>
              </div>
              <div><label className={label}>Branch Filter (optional)</label><input className={input} value={branchFilter} onChange={e => setBranchFilter(e.target.value)} placeholder="main" /></div>
              <div><label className={label}>Playbook (optional)</label>
                <select className={input} value={playbookId} onChange={e => setPlaybookId(e.target.value)}>
                  <option value="">Default code review</option>
                  {playbooks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={createTrigger} className={btnPrimary}>Create Trigger</button>
              <button onClick={() => setShowAdd(false)} className={btnSecondary}>Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={card}>
        <h3 className="text-sm font-bold mb-2">Setup Instructions</h3>
        <ol className="text-xs text-[#A0A0A0] space-y-2 list-decimal list-inside">
          <li>Create a trigger above for your repository</li>
          <li>Copy the webhook secret shown after creation</li>
          <li>In GitHub: Settings → Webhooks → Add webhook</li>
          <li>Payload URL: <code className="text-[#FF4D00]">https://api.getfray.ai/api/github/webhooks</code></li>
          <li>Content type: <code className="text-[#FF4D00]">application/json</code></li>
          <li>Paste the webhook secret</li>
          <li>Select "Pull requests" event</li>
        </ol>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// DEPARTMENT BYOK KEYS (#190)
// ═══════════════════════════════════════════════════════════════════════════════
function DepartmentKeysTab({ workspaceId }) {
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const [deptId, setDeptId] = useState('');
  const [provider, setProvider] = useState('openrouter');
  const [apiKey, setApiKey] = useState('');
  const [keyAlias, setKeyAlias] = useState('');

  const fetchKeys = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    try {
      const [keysRes, usageRes, deptsRes] = await Promise.all([
        API.get(`/api/workspaces/${workspaceId}/department-keys`),
        API.get(`/api/workspaces/${workspaceId}/department-keys/usage`),
        API.get(`/api/enterprise/departments?workspace_id=${workspaceId}`),
      ]);
      setKeys(keysRes.data.keys || []);
      setUsage(usageRes.data.usage || []);
      setDepartments(deptsRes.data.departments || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const addKey = async () => {
    try {
      await API.post(`/api/workspaces/${workspaceId}/department-keys`, {
        department_id: deptId, provider, api_key: apiKey, key_alias: keyAlias,
      });
      setShowAdd(false);
      setApiKey('');
      fetchKeys();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this key?')) return;
    try {
      await API.delete(`/api/workspaces/${workspaceId}/department-keys/${id}`);
      fetchKeys();
    } catch (e) { /* ignore */ }
  };

  if (!workspaceId) return <div className={card}><p className="text-[#A0A0A0] text-sm text-center py-8">Select a workspace to manage department keys.</p></div>;
  if (loading) return <p className="text-[#A0A0A0] text-sm">Loading...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-['Barlow_Condensed']">Department API Keys (BYOK)</h2>
        <button onClick={() => setShowAdd(!showAdd)} className={btnPrimary}><Plus size={14} className="inline mr-1" /> Add Key</button>
      </div>

      {/* Key list */}
      {keys.length === 0 && !showAdd ? (
        <div className={card}><p className="text-[#A0A0A0] text-sm text-center py-8">No department keys configured.</p></div>
      ) : keys.map(k => (
        <motion.div key={k.id} className={card} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Key size={14} className="text-[#FF4D00]" />
                <span className="text-sm font-bold">{k.key_alias || k.provider}</span>
              </div>
              <p className="text-xs text-[#A0A0A0] mt-1">
                {k.department_name || 'Unknown dept'} | {k.provider} | <code>{k.key_preview || '****'}</code>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded ${k.active ? 'bg-[#22C55E20] text-[#22C55E]' : 'bg-[#EF444420] text-[#EF4444]'}`}>
                {k.active ? 'Active' : 'Revoked'}
              </span>
              {k.active && <button onClick={() => revokeKey(k.id)} className="p-2 min-h-[44px] min-w-[44px] text-[#A0A0A0] hover:text-[#EF4444]"><Trash2 size={14} /></button>}
            </div>
          </div>
        </motion.div>
      ))}

      {/* Usage / chargeback */}
      {usage.length > 0 && (
        <div className={card}>
          <h3 className="text-sm font-bold mb-3">Cost Attribution</h3>
          <table className="w-full text-xs">
            <thead><tr className="text-[#A0A0A0] border-b border-[#2A2A2A]">
              <th className="text-left py-2">Department</th><th className="text-left py-2">Provider</th>
              <th className="text-right py-2">Cost</th><th className="text-right py-2">Frays</th>
            </tr></thead>
            <tbody>
              {usage.map((u, i) => (
                <tr key={i} className="border-b border-[#1A1A1A]">
                  <td className="py-2">{u.department_name || '—'}</td>
                  <td className="py-2 text-[#A0A0A0]">{u.provider}</td>
                  <td className="py-2 text-right text-[#FF4D00]">${(u.total_cost_usd || 0).toFixed(4)}</td>
                  <td className="py-2 text-right">{u.fray_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add key form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div className={card} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <h3 className="text-sm font-bold mb-4">Add Department Key</h3>
            <div className="space-y-3">
              <div><label className={label}>Department</label>
                <select className={input} value={deptId} onChange={e => setDeptId(e.target.value)}>
                  <option value="">Select department...</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div><label className={label}>Provider</label>
                <select className={input} value={provider} onChange={e => setProvider(e.target.value)}>
                  <option value="openrouter">OpenRouter</option><option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option><option value="google">Google</option>
                </select>
              </div>
              <div><label className={label}>API Key</label><input className={input} type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="sk-..." /></div>
              <div><label className={label}>Key Alias (optional)</label><input className={input} value={keyAlias} onChange={e => setKeyAlias(e.target.value)} placeholder="Engineering OpenRouter Key" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={addKey} className={btnPrimary}>Add Key</button>
              <button onClick={() => setShowAdd(false)} className={btnSecondary}>Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// BULK EXPORT (#183)
// ═══════════════════════════════════════════════════════════════════════════════
function BulkExportTab({ workspaceId }) {
  const [format, setFormat] = useState('markdown');
  const [limit, setLimit] = useState(100);
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    if (!workspaceId) { alert('Select a workspace'); return; }
    setExporting(true);
    try {
      const res = await API.post(`/api/workspaces/${workspaceId}/export/bulk`, { format, limit }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fray-export.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed: ' + (e.response?.data?.detail || e.message)); }
    setExporting(false);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold font-['Barlow_Condensed']">Bulk Export</h2>

      <div className={card}>
        <p className="text-sm text-[#A0A0A0] mb-4">Export all workspace frays as a ZIP archive. Choose the format below.</p>

        <div className="space-y-3">
          <div><label className={label}>Export Format</label>
            <select className={input} value={format} onChange={e => setFormat(e.target.value)}>
              <option value="markdown">Markdown (.md files)</option>
              <option value="json">JSON (full data)</option>
              <option value="csv">CSV (summary table)</option>
            </select>
          </div>

          <div><label className={label}>Max Frays</label>
            <input className={input} type="number" min={1} max={500} value={limit} onChange={e => setLimit(parseInt(e.target.value) || 100)} />
          </div>
        </div>

        <button onClick={doExport} className={`${btnPrimary} mt-4`} disabled={exporting || !workspaceId}>
          {exporting ? <RefreshCw size={14} className="inline animate-spin mr-1" /> : <Download size={14} className="inline mr-1" />}
          {exporting ? 'Exporting...' : 'Export ZIP'}
        </button>

        {!workspaceId && <p className="text-xs text-[#EF4444] mt-2">Select a workspace to export.</p>}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function EnterpriseSettings() {
  const { user } = useAuth();
  const [tab, setTab] = useState('delivery');
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWs, setSelectedWs] = useState('');

  useEffect(() => {
    API.get('/api/workspaces').then(r => {
      const ws = r.data.workspaces || [];
      setWorkspaces(ws);
      if (ws.length > 0) setSelectedWs(ws[0].id);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl md:text-2xl font-bold font-['Barlow_Condensed']">Enterprise Settings</h1>
          {workspaces.length > 0 && (
            <select
              className="bg-[#1A1A1A] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] min-h-[44px]"
              value={selectedWs} onChange={e => setSelectedWs(e.target.value)}
            >
              {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#2A2A2A] overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 md:px-4 py-2 min-h-[44px] text-xs font-mono flex items-center gap-1.5 transition-colors border-b-2 -mb-px whitespace-nowrap ${
                  tab === t.id ? 'text-[#FF4D00] border-[#FF4D00]' : 'text-[#A0A0A0] border-transparent hover:text-[#F5F5F5]'
                }`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {tab === 'delivery' && <DeliveryTab />}
        {tab === 'sso' && <SSOTab workspaceId={selectedWs} />}
        {tab === 'github-triggers' && <GitHubTriggersTab />}
        {tab === 'department-keys' && <DepartmentKeysTab workspaceId={selectedWs} />}
        {tab === 'bulk-export' && <BulkExportTab workspaceId={selectedWs} />}
        {tab === 'onboarding' && <OnboardingWizardTab workspaceId={selectedWs} />}
        {tab === 'guests' && <GuestAccessTab workspaceId={selectedWs} />}
        {tab === 'ip-allowlist' && <IPAllowlistTab workspaceId={selectedWs} />}
        {tab === 'bulk-import' && <BulkImportTab workspaceId={selectedWs} />}
        {tab === 'classification' && <ClassificationLabelsTab workspaceId={selectedWs} />}
        {tab === 'webhooks' && <WebhooksTab workspaceId={selectedWs} />}
        {tab === 'ropa' && <RoPAExportTab />}
        {tab === 'reports' && <ReportsTab workspaceId={selectedWs} />}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// #268 ENTERPRISE ONBOARDING WIZARD
// ═══════════════════════════════════════════════════════════════════════════════
function OnboardingWizardTab({ workspaceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (workspaceId) load(); }, [workspaceId]);
  const load = async () => {
    setLoading(true);
    try { const r = await API.get(`/api/workspaces/${workspaceId}/onboarding`); setData(r.data); } catch {}
    setLoading(false);
  };
  const completeStep = async (stepId) => { try { await API.post(`/api/workspaces/${workspaceId}/onboarding/${stepId}`, { data: {} }); await load(); } catch {} };
  const skipStep = async (stepId) => { try { await API.post(`/api/workspaces/${workspaceId}/onboarding/skip/${stepId}`); await load(); } catch {} };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#A0A0A0] mx-auto" /></div>;
  if (!data) return <p className="text-sm text-[#A0A0A0]">Unable to load onboarding status.</p>;

  const skippable = new Set(['sso', 'departments', 'models']);
  return (
    <div className="space-y-3">
      <p className="text-xs text-[#A0A0A0] mb-4">Complete each step to configure your enterprise workspace.</p>
      {data.steps?.map((step, i) => {
        const status = data.step_status?.[step.id] || 'pending';
        const isCurrent = data.current_step === step.id;
        return (
          <div key={step.id} className={`${card} ${isCurrent ? '!border-[#FF4D00]/50' : status === 'completed' ? '!border-green-500/30' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono ${
                  status === 'completed' ? 'bg-green-500/20 text-green-400' : status === 'skipped' ? 'bg-yellow-500/20 text-yellow-400' :
                  isCurrent ? 'bg-[#FF4D00]/20 text-[#FF4D00]' : 'bg-[#2A2A2A] text-[#A0A0A0]'
                }`}>{status === 'completed' ? <Check size={14} /> : i + 1}</div>
                <div><p className="text-sm font-medium">{step.name}</p><p className="text-xs text-[#A0A0A0]">{step.description}</p></div>
              </div>
              {status === 'pending' && isCurrent && (
                <div className="flex gap-2">
                  {skippable.has(step.id) && <button onClick={() => skipStep(step.id)} className={`${btnSecondary} !text-xs`}>Skip</button>}
                  <button onClick={() => completeStep(step.id)} className={`${btnPrimary} !text-xs`}>Complete</button>
                </div>
              )}
              {status === 'completed' && <span className="text-[9px] text-green-400 font-mono">DONE</span>}
              {status === 'skipped' && <span className="text-[9px] text-yellow-400 font-mono">SKIPPED</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// #254 GUEST ACCESS
// ═══════════════════════════════════════════════════════════════════════════════
function GuestAccessTab({ workspaceId }) {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', duration_days: 30 });
  const [error, setError] = useState('');

  useEffect(() => { if (workspaceId) load(); }, [workspaceId]);
  const load = async () => { try { const r = await API.get(`/api/workspaces/${workspaceId}/guests`); setGuests(r.data.guests || []); } catch {} setLoading(false); };

  const invite = async () => {
    setError('');
    try { await API.post(`/api/workspaces/${workspaceId}/guests`, form); setShowInvite(false); setForm({ email: '', name: '', duration_days: 30 }); await load(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };
  const revoke = async (id) => { try { await API.post(`/api/workspaces/${workspaceId}/guests/${id}/revoke`); await load(); } catch {} };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#A0A0A0] mx-auto" /></div>;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#A0A0A0]">Invite external guests with time-limited, scoped access.</p>
        <button onClick={() => setShowInvite(true)} className={btnPrimary}><Plus size={14} className="inline mr-1" />Invite Guest</button>
      </div>
      {showInvite && (
        <div className={`${card} mb-4 space-y-3`}>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <input type="email" placeholder="Guest email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={input} />
          <input type="text" placeholder="Name (optional)" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={input} />
          <div className="flex items-center gap-2">
            <label className={label}>Duration (days):</label>
            <input type="number" min={1} max={90} value={form.duration_days} onChange={e => setForm({...form, duration_days: parseInt(e.target.value) || 30})} className={`${input} !w-20`} />
          </div>
          <div className="flex gap-2">
            <button onClick={invite} className={btnPrimary}>Send Invite</button>
            <button onClick={() => setShowInvite(false)} className={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {guests.length === 0 ? <p className="text-sm text-[#A0A0A0] text-center py-8">No guests yet.</p> :
        <div className="space-y-2">{guests.map(g => (
          <div key={g.id} className={`${card} flex items-center justify-between`}>
            <div><p className="text-sm">{g.name || g.email}</p><p className="text-xs text-[#A0A0A0]">{g.email} · Expires {new Date(g.expires_at).toLocaleDateString()}</p></div>
            <div className="flex items-center gap-2">
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono ${g.status === 'active' ? 'bg-green-500/20 text-green-400' : g.status === 'invited' ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'}`}>{g.status}</span>
              {g.status !== 'revoked' && <button onClick={() => revoke(g.id)} className="text-red-400 hover:text-red-300 min-h-[44px] px-1"><X size={14} /></button>}
            </div>
          </div>
        ))}</div>
      }
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// #261 IP ALLOWLISTING
// ═══════════════════════════════════════════════════════════════════════════════
function IPAllowlistTab({ workspaceId }) {
  const [entries, setEntries] = useState([]);
  const [enforced, setEnforced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [cidr, setCidr] = useState('');
  const [lbl, setLbl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { if (workspaceId) load(); }, [workspaceId]);
  const load = async () => { try { const r = await API.get(`/api/workspaces/${workspaceId}/ip-allowlist`); setEntries(r.data.entries || []); setEnforced(r.data.enforced || false); } catch {} setLoading(false); };

  const add = async () => {
    setError('');
    try { await API.post(`/api/workspaces/${workspaceId}/ip-allowlist`, { cidr, label: lbl }); setCidr(''); setLbl(''); setShowAdd(false); await load(); }
    catch (err) { setError(err.response?.data?.detail || 'Invalid CIDR'); }
  };
  const remove = async (id) => { try { await API.delete(`/api/workspaces/${workspaceId}/ip-allowlist/${id}`); await load(); } catch {} };
  const toggleEnforce = async () => {
    try { await API.post(`/api/workspaces/${workspaceId}/ip-allowlist/enforce`, { enabled: !enforced }); setEnforced(!enforced); }
    catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#A0A0A0] mx-auto" /></div>;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div><p className="text-xs text-[#A0A0A0]">Restrict workspace access to specific IP ranges.</p>{error && <p className="text-xs text-red-400 mt-1">{error}</p>}</div>
        <div className="flex items-center gap-2">
          <button onClick={toggleEnforce} className={`text-xs px-3 py-1.5 rounded font-mono min-h-[44px] ${enforced ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-[#2A2A2A] text-[#A0A0A0]'}`}>{enforced ? 'Enforced' : 'Not Enforced'}</button>
          <button onClick={() => setShowAdd(true)} className={btnPrimary}><Plus size={14} className="inline mr-1" />Add Range</button>
        </div>
      </div>
      {showAdd && (
        <div className={`${card} mb-4 space-y-3`}>
          <input type="text" placeholder="IP or CIDR (e.g., 192.168.1.0/24)" value={cidr} onChange={e => setCidr(e.target.value)} className={input} />
          <input type="text" placeholder="Label (e.g., Office VPN)" value={lbl} onChange={e => setLbl(e.target.value)} className={input} />
          <div className="flex gap-2"><button onClick={add} className={btnPrimary}>Add</button><button onClick={() => setShowAdd(false)} className={btnSecondary}>Cancel</button></div>
        </div>
      )}
      {entries.length === 0 ? <p className="text-sm text-[#A0A0A0] text-center py-8">No IP ranges configured.</p> :
        <div className="space-y-2">{entries.map(e => (
          <div key={e.id} className={`${card} flex items-center justify-between`}>
            <div><p className="text-sm font-mono">{e.cidr}</p>{e.label && <p className="text-xs text-[#A0A0A0]">{e.label}</p>}</div>
            <button onClick={() => remove(e.id)} className="text-red-400 hover:text-red-300 min-h-[44px] px-1"><Trash2 size={14} /></button>
          </div>
        ))}</div>
      }
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// #266 BULK USER IMPORT
// ═══════════════════════════════════════════════════════════════════════════════
function BulkImportTab({ workspaceId }) {
  const [csvData, setCsvData] = useState('');
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [tmpl, setTmpl] = useState('');

  useEffect(() => { if (workspaceId) API.get(`/api/workspaces/${workspaceId}/bulk-import/template`).then(r => setTmpl(r.data.template)).catch(() => {}); }, [workspaceId]);

  const run = async () => {
    if (!csvData.trim()) return;
    setImporting(true); setResult(null);
    try { const r = await API.post(`/api/workspaces/${workspaceId}/bulk-import`, { csv_data: csvData }); setResult(r.data); setCsvData(''); }
    catch (err) { setResult({ error: err.response?.data?.detail || 'Import failed' }); }
    setImporting(false);
  };

  return (
    <div>
      <p className="text-xs text-[#A0A0A0] mb-4">Upload a CSV to bulk import users into this workspace.</p>
      <div className={`${card} mb-4`}>
        <div className="flex items-center justify-between mb-2">
          <span className={label}>CSV Template</span>
          <button onClick={() => setCsvData(tmpl)} className="text-xs text-[#FF4D00] hover:underline min-h-[44px]">Use template</button>
        </div>
        <pre className="text-xs font-mono text-[#A0A0A0] bg-[#0F0F0F] p-2 rounded overflow-x-auto">{tmpl}</pre>
      </div>
      <textarea value={csvData} onChange={e => setCsvData(e.target.value)} placeholder="Paste CSV data here" rows={6} className={`${input} !h-auto mb-3`} />
      <button onClick={run} disabled={importing || !csvData.trim()} className={`${btnPrimary} disabled:opacity-50`}>{importing ? 'Importing...' : 'Import Users'}</button>
      {result && (
        <div className={`mt-4 p-4 rounded-lg border ${result.error ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
          {result.error ? <p className="text-sm text-red-400">{result.error}</p> : (
            <div>
              <p className="text-sm text-green-400">Imported {result.imported} of {result.total_rows} users</p>
              {result.errors?.length > 0 && <div className="mt-2 space-y-1">{result.errors.map((e, i) => <p key={i} className="text-xs text-red-400">Line {e.line}: {e.email} — {e.error}</p>)}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// #270 CONTENT CLASSIFICATION LABELS
// ═══════════════════════════════════════════════════════════════════════════════
function ClassificationLabelsTab({ workspaceId }) {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', color: '#808080', level: 0, description: '' });

  useEffect(() => { if (workspaceId) load(); }, [workspaceId]);
  const load = async () => { try { const r = await API.get(`/api/workspaces/${workspaceId}/classification-labels`); setLabels(r.data.labels || []); } catch {} setLoading(false); };
  const create = async () => { try { await API.post(`/api/workspaces/${workspaceId}/classification-labels`, form); setShowAdd(false); setForm({ name: '', color: '#808080', level: 0, description: '' }); await load(); } catch {} };
  const del = async (id) => { try { await API.delete(`/api/workspaces/${workspaceId}/classification-labels/${id}`); await load(); } catch {} };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#A0A0A0] mx-auto" /></div>;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#A0A0A0]">Define classification labels for content sensitivity levels.</p>
        <button onClick={() => setShowAdd(true)} className={btnPrimary}><Plus size={14} className="inline mr-1" />Add Label</button>
      </div>
      {showAdd && (
        <div className={`${card} mb-4 space-y-3`}>
          <div className="flex gap-2">
            <input type="text" placeholder="Label name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={`${input} flex-1`} />
            <input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="w-12 h-[44px] bg-[#0F0F0F] border border-[#2A2A2A] rounded cursor-pointer" />
          </div>
          <div className="flex gap-2">
            <input type="number" placeholder="Level" value={form.level} onChange={e => setForm({...form, level: parseInt(e.target.value) || 0})} className={`${input} !w-24`} />
            <input type="text" placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className={`${input} flex-1`} />
          </div>
          <div className="flex gap-2"><button onClick={create} className={btnPrimary}>Create</button><button onClick={() => setShowAdd(false)} className={btnSecondary}>Cancel</button></div>
        </div>
      )}
      <div className="space-y-2">{labels.map(lb => (
        <div key={lb.id} className={`${card} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: lb.color }} />
            <div><p className="text-sm font-medium">{lb.name}</p><p className="text-xs text-[#A0A0A0]">Level {lb.level}{lb.description ? ` · ${lb.description}` : ''}</p></div>
          </div>
          <button onClick={() => del(lb.id)} className="text-red-400 hover:text-red-300 min-h-[44px] px-1"><Trash2 size={14} /></button>
        </div>
      ))}</div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// #271 WEBHOOKS
// ═══════════════════════════════════════════════════════════════════════════════
function WebhooksTab({ workspaceId }) {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ url: '', event_types: [], description: '' });
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(null);

  useEffect(() => { if (workspaceId) load(); }, [workspaceId]);
  const load = async () => { try { const r = await API.get(`/api/workspaces/${workspaceId}/webhooks`); setWebhooks(r.data.webhooks || []); setEvents(r.data.available_events || []); } catch {} setLoading(false); };
  const toggleEvent = (ev) => setForm(f => ({ ...f, event_types: f.event_types.includes(ev) ? f.event_types.filter(e => e !== ev) : [...f.event_types, ev] }));

  const create = async () => {
    setError('');
    if (!form.url || !form.event_types.length) { setError('URL and events required'); return; }
    try { await API.post(`/api/workspaces/${workspaceId}/webhooks`, form); setShowAdd(false); setForm({ url: '', event_types: [], description: '' }); await load(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => { try { await API.delete(`/api/workspaces/${workspaceId}/webhooks/${id}`); await load(); } catch {} };
  const test = async (id) => {
    setTesting(id);
    try { const r = await API.post(`/api/workspaces/${workspaceId}/webhooks/${id}/test`); alert(r.data.success ? `Test passed (${r.data.status_code})` : `Failed: ${r.data.error || r.data.status_code}`); } catch {}
    setTesting(null);
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#A0A0A0] mx-auto" /></div>;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#A0A0A0]">Configure outbound webhooks for enterprise events.</p>
        <button onClick={() => setShowAdd(true)} className={btnPrimary}><Plus size={14} className="inline mr-1" />Add Webhook</button>
      </div>
      {showAdd && (
        <div className={`${card} mb-4 space-y-3`}>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <input type="url" placeholder="https://example.com/webhook" value={form.url} onChange={e => setForm({...form, url: e.target.value})} className={input} />
          <input type="text" placeholder="Description (optional)" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className={input} />
          <div><p className="text-xs text-[#A0A0A0] mb-2">Event types:</p>
            <div className="flex flex-wrap gap-1.5">{events.map(ev => (
              <button key={ev} onClick={() => toggleEvent(ev)} className={`text-[10px] px-2 py-1 rounded font-mono transition-colors ${form.event_types.includes(ev) ? 'bg-[#FF4D00]/20 text-[#FF4D00] border border-[#FF4D00]/40' : 'bg-[#0F0F0F] text-[#A0A0A0] border border-[#2A2A2A]'}`}>{ev}</button>
            ))}</div>
          </div>
          <div className="flex gap-2"><button onClick={create} className={btnPrimary}>Create</button><button onClick={() => setShowAdd(false)} className={btnSecondary}>Cancel</button></div>
        </div>
      )}
      {webhooks.length === 0 ? <p className="text-sm text-[#A0A0A0] text-center py-8">No webhooks configured.</p> :
        <div className="space-y-2">{webhooks.map(wh => (
          <div key={wh.id} className={card}>
            <div className="flex items-center justify-between mb-2">
              <div><p className="text-sm font-mono truncate max-w-xs">{wh.url}</p>{wh.description && <p className="text-xs text-[#A0A0A0]">{wh.description}</p>}</div>
              <div className="flex items-center gap-2">
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${wh.enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{wh.enabled ? 'active' : 'off'}</span>
                <button onClick={() => test(wh.id)} disabled={testing === wh.id} className="text-xs text-[#3B82F6] hover:underline min-h-[44px] px-1">{testing === wh.id ? '...' : 'Test'}</button>
                <button onClick={() => del(wh.id)} className="text-red-400 hover:text-red-300 min-h-[44px] px-1"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">{(typeof wh.event_types === 'string' ? JSON.parse(wh.event_types) : wh.event_types || []).map(ev =>
              <span key={ev} className="text-[9px] bg-[#0F0F0F] text-[#A0A0A0] px-1.5 py-0.5 rounded font-mono">{ev}</span>
            )}</div>
          </div>
        ))}</div>
      }
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// #272 ROPA EXPORT
// ═══════════════════════════════════════════════════════════════════════════════
function RoPAExportTab() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { API.get('/api/enterprise/ropa').then(r => setActivities(r.data.activities || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  const exportRopa = async (format) => {
    setExporting(true);
    try {
      if (format === 'csv') {
        const r = await API.post('/api/enterprise/ropa/export', { format: 'csv' }, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a'); a.href = url; a.download = 'ropa-export.csv'; a.click(); window.URL.revokeObjectURL(url);
      } else {
        const r = await API.post('/api/enterprise/ropa/export', { format: 'json' });
        const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'ropa-export.json'; a.click(); window.URL.revokeObjectURL(url);
      }
    } catch {} setExporting(false);
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#A0A0A0] mx-auto" /></div>;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#A0A0A0]">Records of Processing Activities (GDPR Article 30)</p>
        <div className="flex gap-2">
          <button onClick={() => exportRopa('csv')} disabled={exporting} className={btnSecondary}><Download size={14} className="inline mr-1" />CSV</button>
          <button onClick={() => exportRopa('json')} disabled={exporting} className={btnPrimary}><Download size={14} className="inline mr-1" />JSON</button>
        </div>
      </div>
      <div className="space-y-3">{activities.map(act => (
        <div key={act.id} className={card}>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">{act.activity}</h4>
            <span className="text-[9px] font-mono text-[#A0A0A0] bg-[#0F0F0F] px-1.5 py-0.5 rounded">{act.id}</span>
          </div>
          <p className="text-xs text-[#A0A0A0] mb-3">{act.purpose}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <div><span className="text-[#A0A0A0]">Legal basis:</span> {act.legal_basis}</div>
            <div><span className="text-[#A0A0A0]">Retention:</span> {act.retention}</div>
            <div><span className="text-[#A0A0A0]">Data categories:</span> {act.data_categories?.join(', ')}</div>
            <div><span className="text-[#A0A0A0]">Recipients:</span> {act.recipients?.join(', ')}</div>
          </div>
        </div>
      ))}</div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// #273 REPORTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function ReportsTab({ workspaceId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30d');
  const [boardPack, setBoardPack] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await API.get(`/api/enterprise/reports/executive-summary?workspace_id=${workspaceId}&period=${period}`);
      setReport(r.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [workspaceId, period]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const generateBoardPack = async () => {
    setExporting(true);
    try {
      const r = await API.post(`/api/enterprise/reports/board-pack?workspace_id=${workspaceId}&period=${period}`);
      setBoardPack(r.data.report);
    } catch (e) { /* ignore */ }
    setExporting(false);
  };

  const downloadBoardPack = () => {
    if (!boardPack) return;
    const blob = new Blob([boardPack], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `board-pack-${period}.md`; a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0]"><Loader2 className="w-4 h-4 animate-spin" /> Loading report...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-mono text-[#FF4D00]">Executive Summary</h3>
        <div className="flex items-center gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1 text-xs text-[#F5F5F5]">
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button onClick={fetchReport} className={btnSecondary + ' text-xs !py-1 !px-2 !min-h-0'}>
            <RefreshCw className="w-3 h-3 inline mr-1" />Refresh
          </button>
        </div>
      </div>

      {report?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { label: 'Total Frays', value: report.summary.total_frays || 0 },
            { label: 'Completed', value: report.summary.completed_frays || 0 },
            { label: 'Failed', value: report.summary.failed_frays || 0 },
            { label: 'Total Cost', value: `$${parseFloat(report.summary.total_cost_usd || 0).toFixed(2)}` },
            { label: 'Avg Duration', value: `${Math.round(report.summary.avg_duration_seconds || 0)}s` },
            { label: 'Total Tokens', value: (report.summary.total_tokens || 0).toLocaleString() },
          ].map(s => (
            <div key={s.label} className={card}>
              <div className="text-[10px] font-mono text-[#A0A0A0] mb-1">{s.label}</div>
              <div className="text-lg font-bold text-[#F5F5F5]">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {report?.top_playbooks?.length > 0 && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#A0A0A0] mb-2">Top Playbooks</h4>
          <div className="space-y-1">
            {report.top_playbooks.map((p, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>{p.template_name || p.template_slug}</span>
                <span className="text-[#A0A0A0]">{p.fray_count} frays / ${parseFloat(p.total_cost || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {report?.top_users?.length > 0 && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#A0A0A0] mb-2">Top Users</h4>
          <div className="space-y-1">
            {report.top_users.map((u, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>{u.user_name || u.email || 'Unknown'}</span>
                <span className="text-[#A0A0A0]">{u.fray_count} frays / ${parseFloat(u.total_cost || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={card}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-mono text-[#A0A0A0]">Board Pack Export (#274)</h4>
          <div className="flex items-center gap-2">
            <button onClick={generateBoardPack} disabled={exporting} className={btnPrimary + ' text-xs !py-1 !px-3 !min-h-0'}>
              {exporting ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : <FileText className="w-3 h-3 inline mr-1" />}
              Generate
            </button>
            {boardPack && (
              <button onClick={downloadBoardPack} className={btnSecondary + ' text-xs !py-1 !px-3 !min-h-0'}>
                <Download className="w-3 h-3 inline mr-1" />Download .md
              </button>
            )}
          </div>
        </div>
        {boardPack && (
          <pre className="bg-[#0F0F0F] border border-[#2A2A2A] rounded p-3 text-xs text-[#A0A0A0] overflow-auto max-h-96 whitespace-pre-wrap">{boardPack}</pre>
        )}
      </div>
    </div>
  );
}
